"use client";

import React, {
  useCallback,
  FC,
  useMemo,
  MouseEventHandler,
  ChangeEventHandler,
  ChangeEvent,
} from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { has } from "lodash";

import { FacetCountsFacetFieldsCategory } from "~/bff/types/FacetCountsFacetFieldsCategory";
import { FILTER_DIVIDER } from "~/components/products-layout/constants";
import { VirtualList } from "~/components/virtual-list/component";
import { CheckMark24 } from "~/theme/icons/components/check-mark";
import { Nullable } from "~/types/general.types";

import { AppliedFilters, HandleFiltersCallback } from "../../../../types";
import { FILTERS_CHECKBOX_SHARED_CLASSES } from "../filter-shared.constants";
import { SwatchFilterButtonContainer } from "../filter-shared.styled";

export interface SwatchFilterProps {
  filters?: FacetCountsFacetFieldsCategory[];
  label: string;
  appliedFilters?: Nullable<AppliedFilters>;
  onClick?: HandleFiltersCallback;
}

const getEstimatedHeight = () => 48;

export const SwatchFilter: FC<SwatchFilterProps> = ({
  label,
  filters,
  appliedFilters,
  onClick,
}) => {
  const getKey = useCallback(
    (data: FacetCountsFacetFieldsCategory) => `${data.cat_id}`,
    [],
  );

  const handleSetSelectedSwatchFilter = useCallback(
    (newFilter: string) => (event: React.ChangeEventHandler<HTMLInputElement>) => {
      if (onClick) {
        onClick(
          event as ChangeEventHandler<HTMLInputElement> &
            ChangeEvent<HTMLInputElement>,
          newFilter,
        );
      }
    },
    [onClick],
  );

  const renderItem = useCallback(
    (data: FacetCountsFacetFieldsCategory) => {
      const { count, cat_id, cat_name } = data ?? {};
      const filterObjectKey = `${label}${FILTER_DIVIDER}${cat_name}${FILTER_DIVIDER}${cat_id}${FILTER_DIVIDER}swatch`;
      return (
        <SwatchFilterButtonContainer
          key={cat_id}
          variant="text"
          value={count || undefined}
          onClick={
            handleSetSelectedSwatchFilter(
              filterObjectKey,
            ) as unknown as MouseEventHandler<HTMLButtonElement>
          }
        >
          {has(appliedFilters, filterObjectKey) && (
            <Box className={FILTERS_CHECKBOX_SHARED_CLASSES.selectedCategory}>
              <CheckMark24 />
            </Box>
          )}
          <Typography variant="body2">{cat_name}</Typography>
          <Typography
            variant="body2"
            className={FILTERS_CHECKBOX_SHARED_CLASSES.numberOfItems}
          >
            {count}
          </Typography>
        </SwatchFilterButtonContainer>
      );
    },
    [handleSetSelectedSwatchFilter, label, appliedFilters],
  );

  const lOneFiltersOnly = useMemo(() => {
    return filters?.filter(({ parent }) => parent === "");
  }, [filters]);

  return (
    <VirtualList
      items={lOneFiltersOnly || []}
      renderItem={renderItem}
      getKey={getKey}
      estimateSize={getEstimatedHeight}
    />
  );
};
